import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEye, FaPlay, FaSpinner, FaSearch, FaFilter, FaUser, FaGlobe, FaVideo, FaClock, FaCamera, FaCheck, FaTimes, FaDownload, FaInfoCircle, FaMoneyBillWave, FaMapMarkerAlt, FaCalendar, FaBuilding, FaFileAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ViewChecker from "../../Common/ViewChecker";

const RunningPost = () => {
  const [runningAds, setRunningAds] = useState([]);
  const [completedAds, setCompletedAds] = useState([]);
  const [currentViewAds, setCurrentViewAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState("");
  const [searchText, setSearchText] = useState("");
  const [checkingViews, setCheckingViews] = useState({});
  const [adViews, setAdViews] = useState({});
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;
  const [processingProof, setProcessingProof] = useState({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [showCompletedAds, setShowCompletedAds] = useState(false);
  const [showAdDetailsModal, setShowAdDetailsModal] = useState(false);
  const [adDetails, setAdDetails] = useState(null);
  const [loadingAdDetails, setLoadingAdDetails] = useState(false);

  // Function to filter ads based on search text
  // Robust search logic: normalize and check all relevant fields
  const getFilteredAds = () => {
    const q = searchText.trim().toLowerCase();

    // Combine running ads with completed ads if toggle is on
    const allAds = showCompletedAds ? [...runningAds, ...completedAds] : runningAds;

    if (!q) return allAds;
    const filtered = allAds.filter((ad) => {
      // Check adId (handle both string and object cases)
      const adIdString = typeof ad.adId === 'object' ? ad.adId._id : ad.adId;
      if (adIdString && adIdString.toString().trim().toLowerCase().includes(q)) return true;
      // Check all relevant fields in proofs
      if (
        ad.proofs &&
        ad.proofs.some((proof) => {
          const iinsafId = proof.iinsafId ? proof.iinsafId.toString().trim().toLowerCase() : "";
          const channelName = proof.channelName ? proof.channelName.trim().toLowerCase() : "";
          const reporterName = proof.reporterName ? proof.reporterName.trim().toLowerCase() : "";
          const platform = proof.platform ? proof.platform.trim().toLowerCase() : "";
          const duration = proof.duration ? proof.duration.toString().trim().toLowerCase() : "";
          const submittedAt = proof.submittedAt ? new Date(proof.submittedAt).toLocaleDateString("en-GB").toLowerCase() : "";
          return (
            iinsafId.includes(q) ||
            channelName.includes(q) ||
            reporterName.includes(q) ||
            platform.includes(q) ||
            duration.includes(q) ||
            submittedAt.includes(q)
          );
        })
      ) {
        return true;
      }
      return false;
    });
    return filtered;
  };

  // Pagination logic
  const filteredAds = getFilteredAds();
  const totalAds = filteredAds.length;
  const totalPages = Math.ceil(totalAds / rowsPerPage);
  // Ensure currentPage is always valid
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages || 1);
  }, [totalPages, currentPage]);

  // Sort ads by date (latest first) - use createdAt or updatedAt from adDetails
  const sortedAds = [...filteredAds].sort((a, b) => {
    const dateA = a.adDetails?.createdAt || a.adDetails?.updatedAt || a.createdAt || a.updatedAt || 0;
    const dateB = b.adDetails?.createdAt || b.adDetails?.updatedAt || b.createdAt || b.updatedAt || 0;
    return new Date(dateB) - new Date(dateA); // Latest first
  });

  // Get ads for current page
  const paginatedAds = sortedAds.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to table for better UX on mobile
      const table = document.getElementById('ads-table');
      if (table) table.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const fetchRunningAds = async () => {
    setLoading(true);
    setError(null);
    try {

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/all/running/ads`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Fetched Running Advertisements Data:", response);
      let runningAdsData = response.data.data || [];

      // ✅ Normalize data: If adId is populated (object), extract just the ID
      runningAdsData = runningAdsData.map(ad => {
        if (ad.adId && typeof ad.adId === 'object') {
          // adId is populated, extract the ID and keep ad details
          const adDetails = ad.adId;
          return {
            ...ad,
            adId: adDetails._id, // Use just the ID for adId field
            adDetails: adDetails, // Store full ad details separately
            requiredReporter: adDetails.requiredReporter || ad.requiredReporter,
            finalReporterPrice: adDetails.finalReporterPrice || ad.finalReporterPrice,
            baseView: adDetails.baseView || ad.baseView
          };
        }
        return ad;
      });

      // Debug: Log proof details
      console.log("Running ads data:", runningAdsData);
      runningAdsData.forEach((ad, adIndex) => {
        console.log(`Ad ${adIndex + 1} (${ad.adId}):`, {
          runningAdStatus: ad.runningAdStatus,
          proofs: ad.proofs?.map((proof, proofIndex) => ({
            proofIndex,
            reporterId: proof.reporterId,
            status: proof.status,
            hasScreenshot: !!proof.screenshot,
            hasCompletedTaskScreenshot: !!proof.completedTaskScreenshot,
            completedTaskScreenshot: proof.completedTaskScreenshot
          }))
        });
      });

      // Filter out ads where all proofs are completed
      const activeRunningAds = runningAdsData.filter(ad =>
        !ad.proofs || ad.proofs.length === 0 ||
        ad.proofs.some(proof => proof.status !== "completed")
      );

      const completedAds = runningAdsData.filter(ad =>
        ad.proofs && ad.proofs.length > 0 &&
        ad.proofs.every(proof => proof.status === "completed")
      );

      console.log("Active running ads:", activeRunningAds.length);
      console.log("Completed ads:", completedAds.length);

      setRunningAds(activeRunningAds);
      setCompletedAds(completedAds);
    } catch (err) {
      setError("Failed to fetch running ads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRunningAds();
  }, []);

  // Add refresh function
  const refreshData = () => {
    fetchRunningAds();
  };

  // Function to check current view count
  const handleCheckCurrentView = async (platform, videoLink, adId, proofIndex) => {
    const uniqueKey = `${adId}-${proofIndex}`;

    try {
      if (!platform || !videoLink) {
        toast.error("Platform or video link is missing");
        return;
      }

      // Set loading state for this specific button
      setCheckingViews(prev => ({ ...prev, [uniqueKey]: true }));

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/check/current/view`,
        {
          platform: platform,
          videoUrl: videoLink
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log("Current view response:", response.data);

      if (response.data.success) {
        // Store the views for this specific proof
        setAdViews(prev => ({ ...prev, [uniqueKey]: response.data.views }));

      } else {
        toast.error("Failed to fetch current views");
      }
    } catch (error) {
      console.error('Error checking current view:', error);
      toast.error("Failed to check current views. Please try again.");
    } finally {
      // Remove loading state
      setCheckingViews(prev => ({ ...prev, [uniqueKey]: false }));
    }
  };

  const handleApproveProof = async (adId, reporterId) => {
    // Show confirmation modal
    setConfirmAction('approve');
    setConfirmData({ adId, reporterId });
    setShowConfirmModal(true);
  };

  const handleApproveInitialProof = async (adId, reporterId) => {
    // Show confirmation modal for initial proof
    setConfirmAction('approve_initial');
    setConfirmData({ adId, reporterId });
    setShowConfirmModal(true);
  };

  const handleRejectInitialProof = async (adId, reporterId) => {
    // Show confirmation modal for initial proof rejection
    setConfirmAction('reject_initial');
    setConfirmData({ adId, reporterId });
    setAdminNote(""); // Reset admin note
    setShowConfirmModal(true);
  };

  const confirmApproveProof = async () => {
    const { adId, reporterId } = confirmData;
    const key = `${adId}-${reporterId}`;
    setProcessingProof(prev => ({ ...prev, [key]: true }));

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/approve/completed/ad`,
        { adId, reporterId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Proof approved successfully! Wallet credited.");
        // Refresh data to update the UI
        fetchRunningAds();
        // Navigate to completed posts page after a short delay
        setTimeout(() => {
          navigate("/admin/completedposts");
        }, 1500);
      }
    } catch (error) {
      console.error('Error approving proof:', error);
      toast.error('Failed to approve proof');
    } finally {
      setProcessingProof(prev => ({ ...prev, [key]: false }));
      setShowConfirmModal(false);
      setConfirmAction(null);
      setConfirmData(null);
    }
  };

  const handleRejectProof = async (adId, reporterId) => {
    // Show confirmation modal
    setConfirmAction('reject');
    setConfirmData({ adId, reporterId });
    setAdminNote(""); // Reset admin note
    setShowConfirmModal(true);
  };

  const confirmApproveInitialProof = async () => {
    const { adId, reporterId } = confirmData;
    const key = `${adId}-${reporterId}`;
    setProcessingProof(prev => ({ ...prev, [key]: true }));

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/approve/initial/proof`,
        { adId, reporterId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success(response.data?.message || "Initial proof approved! User can proceed with the task.");
        // Refresh data to update the UI
        fetchRunningAds();
      }
    } catch (error) {
      console.error('Error approving initial proof:', error);
      toast.error('Failed to approve initial proof');
    } finally {
      setProcessingProof(prev => ({ ...prev, [key]: false }));
      setShowConfirmModal(false);
      setConfirmAction(null);
      setConfirmData(null);
    }
  };

  const confirmRejectInitialProof = async () => {
    if (!adminNote.trim()) {
      toast.error("Please provide a reason for rejecting the initial proof");
      return;
    }

    const { adId, reporterId } = confirmData;
    const key = `${adId}-${reporterId}`;
    setProcessingProof(prev => ({ ...prev, [key]: true }));

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/reject/initial/proof`,
        { adId, reporterId, adminNote: adminNote.trim() },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success(response.data?.message || "Initial proof rejected. User must resubmit.");
        // Refresh data to update the UI
        fetchRunningAds();
      }
    } catch (error) {
      console.error('Error rejecting initial proof:', error);
      toast.error('Failed to reject initial proof');
    } finally {
      setProcessingProof(prev => ({ ...prev, [key]: false }));
      setShowConfirmModal(false);
      setConfirmAction(null);
      setConfirmData(null);
      setAdminNote("");
    }
  };

  const confirmRejectProof = async () => {
    if (!adminNote.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    const { adId, reporterId } = confirmData;
    const key = `${adId}-${reporterId}`;
    setProcessingProof(prev => ({ ...prev, [key]: true }));

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/reject/completed/ad`,
        { adId, reporterId, adminNote: adminNote.trim() },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Proof rejected successfully. User can resubmit.");
        // Stay on running posts page for resubmission
        fetchRunningAds();
      }
    } catch (error) {
      console.error('Error rejecting proof:', error);
      toast.error('Failed to reject proof');
    } finally {
      setProcessingProof(prev => ({ ...prev, [key]: false }));
      setShowConfirmModal(false);
      setConfirmAction(null);
      setConfirmData(null);
      setAdminNote("");
    }
  };

  const handlePreviewProof = (proof, ad) => {
    setSelectedProof({ ...proof, adDetails: ad });
    setShowPreviewModal(true);
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setSelectedProof(null);
  };

  // Function to fetch full advertisement details
  const handleViewAdDetails = async (adId) => {
    setLoadingAdDetails(true);
    setShowAdDetailsModal(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/full/advertisement-details/${adId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        setAdDetails(response.data.data);
      } else {
        toast.error("Failed to fetch advertisement details");
        setShowAdDetailsModal(false);
      }
    } catch (error) {
      console.error("Error fetching advertisement details:", error);
      toast.error("Failed to fetch advertisement details");
      setShowAdDetailsModal(false);
    } finally {
      setLoadingAdDetails(false);
    }
  };

  const closeAdDetailsModal = () => {
    setShowAdDetailsModal(false);
    setAdDetails(null);
  };

  return (
    <>
      {/* Screenshot Modal */}
      {showScreenshotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative border border-gray-200">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 text-white p-6 rounded-t-2xl relative">
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold bg-white bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                onClick={() => setShowScreenshotModal(false)}
                title="Close"
              >
                &times;
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <FaCamera className="w-6 h-6 text-blue-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">
                    Screenshot Preview
                  </h3>
                  <p className="text-blue-100 text-sm">
                    View the submitted proof screenshot
                  </p>
                </div>
              </div>
            </div>

            {/* Image Content */}
            <div className="p-8">
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <div className="flex items-center justify-center min-h-[400px] bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <img
                    src={currentScreenshot}
                    alt="Screenshot"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/fallback-image.jpg";
                    }}
                    className="max-h-[70vh] max-w-full rounded-lg shadow-sm border border-gray-200 object-contain transition-transform duration-300 hover:scale-105"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={() => setShowScreenshotModal(false)}
                  className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedProof && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto relative border border-gray-200">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-700 text-white p-6 rounded-t-2xl relative">
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold bg-white bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                onClick={closePreviewModal}
                title="Close"
              >
                &times;
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <FaEye className="w-6 h-6 text-purple-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">
                    Proof Details Preview
                  </h3>
                  <p className="text-purple-100 text-sm">
                    Complete information about this proof submission
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8">

              {/* Proof Status Banner */}
              {selectedProof.status === "pending" && (
                <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-yellow-500 text-white rounded-full p-2">
                      <FaCamera className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-800">⏳ Initial Proof - Pending Approval</h4>
                      <p className="text-sm text-gray-600">This is the user's first submission. Approve to allow them to proceed with the task.</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedProof.status === "approved" && (
                <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-5">
                  <div className="flex items-start gap-4">
                    <div className="bg-green-500 text-white rounded-full p-3">
                      <FaCheck className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-gray-800 mb-2">✅ Initial Proof Approved</h4>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-700 flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          User's first submission has been approved
                        </p>
                        <p className="text-sm text-gray-700 flex items-center gap-2">
                          <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                          <strong>Waiting for final proof submission</strong>
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          Once the user completes the task and submits the final screenshot, it will appear here for your review.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedProof.status === "submitted" && (
                <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-500 text-white rounded-full p-2">
                      <FaCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-800">📸 Final Proof - Pending Approval</h4>
                      <p className="text-sm text-gray-600">User has completed the task and submitted final proof. Review to approve or reject.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Information Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {/* User Information Card */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-purple-500 text-white rounded-lg p-2">
                      <FaUser className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">User Information</h5>
                      <p className="text-sm text-gray-600">User details and identification</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">User ID</span>
                      <span className="font-mono text-sm text-gray-900 break-all">{selectedProof.reporterId}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">IINSAF ID</span>
                      <span className="font-mono text-sm text-gray-900">{selectedProof.iinsafId || "N/A"}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">User Type</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedProof.iinsafId && selectedProof.iinsafId.startsWith('INF')
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                        }`}>
                        {selectedProof.iinsafId && selectedProof.iinsafId.startsWith('INF') ? '🎯 Influencer' : '📰 Reporter'}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Channel Name</span>
                      <span className="font-medium text-gray-900">{selectedProof.channelName || "N/A"}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Platform</span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {selectedProof.platform || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Work Details Card */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-500 text-white rounded-lg p-2">
                      <FaGlobe className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Work Details</h5>
                      <p className="text-sm text-gray-600">Advertisement and task information</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad ID</span>
                      <span className="font-mono text-sm text-gray-900 break-all">
                        {typeof selectedProof.adDetails?.adId === 'object'
                          ? selectedProof.adDetails?.adId?._id
                          : selectedProof.adDetails?.adId}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad Type</span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                        {selectedProof.adDetails?.adType || selectedProof.adDetails?.adId?.adType || 'N/A'}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Duration</span>
                      <span className="font-medium text-gray-900">{selectedProof.duration || "N/A"}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Status</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedProof.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {selectedProof.status}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Task Status</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedProof.task === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-orange-100 text-orange-800'
                        }`}>
                        {selectedProof.task}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Submitted At</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedProof.submittedAt ? new Date(selectedProof.submittedAt).toLocaleString() : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment Information Card */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-500 text-white rounded-lg p-2">
                      <span className="text-sm font-bold">₹</span>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Payment Information</h5>
                      <p className="text-sm text-gray-600">Financial details and rewards</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Wallet Credit</span>
                      <span className="text-2xl font-bold text-green-600">
                        ₹{selectedProof.adDetails?.finalReporterPrice || selectedProof.adDetails?.adId?.finalReporterPrice
                          ? Number(selectedProof.adDetails?.finalReporterPrice || selectedProof.adDetails?.adId?.finalReporterPrice).toFixed(2)
                          : "N/A"}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Base View</span>
                      <span className="text-xl font-bold text-green-600">
                        {(selectedProof.adDetails?.baseView || selectedProof.adDetails?.adId?.baseView)?.toLocaleString() || "N/A"}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Required Reporters</span>
                      <span className="text-lg font-bold text-green-600">
                        {selectedProof.adDetails?.requiredReporter || selectedProof.adDetails?.adId?.requiredReporter || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Video Information Card */}
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-2xl border border-indigo-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-indigo-500 text-white rounded-lg p-2">
                      <FaVideo className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Video Information</h5>
                      <p className="text-sm text-gray-600">Video content and links</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-indigo-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-2">Video Link</span>
                      {selectedProof.videoLink ? (
                        <a
                          href={selectedProof.videoLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium transition-colors shadow-sm hover:shadow-md"
                        >
                          <FaPlay className="w-3 h-3" />
                          Watch Video
                        </a>
                      ) : (
                        <span className="text-gray-500 text-sm">No video link provided</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Screenshots Section */}
              {(selectedProof.screenshot || selectedProof.completedTaskScreenshot) && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-2xl border border-gray-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-gray-500 text-white rounded-lg p-2">
                      <FaCamera className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Screenshots</h5>
                      <p className="text-sm text-gray-600">Proof and task completion screenshots</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {selectedProof.screenshot && (
                      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                        <h6 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                          <FaCamera className="w-4 h-4 text-blue-500" />
                          Proof Screenshot
                        </h6>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="text-xs text-gray-500 mb-2">
                            URL: {selectedProof.screenshot.startsWith('http')
                              ? selectedProof.screenshot
                              : `${process.env.REACT_APP_API_BASE_URL}/${selectedProof.screenshot.replace(/\\/g, "/")}`}
                          </div>
                          <img
                            src={selectedProof.screenshot.startsWith('http')
                              ? selectedProof.screenshot
                              : `${process.env.REACT_APP_API_BASE_URL}/${selectedProof.screenshot.replace(/\\/g, "/")}`}
                            alt="Proof Screenshot"
                            className="max-w-full h-auto rounded-lg shadow-sm border border-gray-200"
                            onError={(e) => {
                              console.error("Screenshot load error:", selectedProof.screenshot);
                              e.target.onerror = null;
                              e.target.src = "/fallback-image.jpg";
                            }}
                            onLoad={(e) => {
                              console.log("Screenshot loaded successfully:", selectedProof.screenshot);
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {selectedProof.completedTaskScreenshot && (
                      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                        <h6 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                          <FaCheck className="w-4 h-4 text-green-500" />
                          Completed Task Screenshot
                        </h6>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="text-xs text-gray-500 mb-2">
                            URL: {selectedProof.completedTaskScreenshot.startsWith('http')
                              ? selectedProof.completedTaskScreenshot
                              : `${process.env.REACT_APP_API_BASE_URL}/${selectedProof.completedTaskScreenshot.replace(/\\/g, "/")}`}
                          </div>
                          <img
                            src={selectedProof.completedTaskScreenshot.startsWith('http')
                              ? selectedProof.completedTaskScreenshot
                              : `${process.env.REACT_APP_API_BASE_URL}/${selectedProof.completedTaskScreenshot.replace(/\\/g, "/")}`}
                            alt="Completed Task Screenshot"
                            className="max-w-full h-auto rounded-lg shadow-sm border border-gray-200"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/fallback-image.jpg";
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Rejection Note */}
              {selectedProof.rejectNote && (
                <div className="mt-6 bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-2xl border border-red-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-red-500 text-white rounded-lg p-2">
                      <FaTimes className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Rejection Note</h5>
                      <p className="text-sm text-gray-600">Reason for rejection</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-red-200">
                    <p className="text-gray-700">{selectedProof.rejectNote}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-4">
                {/* Show Accept/Reject buttons for PENDING initial proof */}
                {selectedProof.status === "pending" && (
                  <>
                    <button
                      onClick={() => {
                        closePreviewModal();
                        handleApproveInitialProof(
                          typeof selectedProof.adDetails?.adId === 'object'
                            ? selectedProof.adDetails?.adId?._id
                            : selectedProof.adDetails?.adId,
                          selectedProof.reporterId
                        );
                      }}
                      className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center gap-2"
                      disabled={processingProof[`${selectedProof.adDetails?.adId}-${selectedProof.reporterId}`]}
                    >
                      <FaCheck className="w-4 h-4" />
                      Accept Initial Proof
                    </button>
                    <button
                      onClick={() => {
                        closePreviewModal();
                        handleRejectInitialProof(
                          typeof selectedProof.adDetails?.adId === 'object'
                            ? selectedProof.adDetails?.adId?._id
                            : selectedProof.adDetails?.adId,
                          selectedProof.reporterId
                        );
                      }}
                      className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center gap-2"
                      disabled={processingProof[`${selectedProof.adDetails?.adId}-${selectedProof.reporterId}`]}
                    >
                      <FaTimes className="w-4 h-4" />
                      Reject Initial Proof
                    </button>
                  </>
                )}

                {/* Show Accept/Reject buttons for SUBMITTED final proof */}
                {selectedProof.status === "submitted" && (
                  <>
                    <button
                      onClick={() => {
                        closePreviewModal();
                        handleApproveProof(
                          typeof selectedProof.adDetails?.adId === 'object'
                            ? selectedProof.adDetails?.adId?._id
                            : selectedProof.adDetails?.adId,
                          selectedProof.reporterId
                        );
                      }}
                      className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center gap-2"
                      disabled={processingProof[`${selectedProof.adDetails?.adId}-${selectedProof.reporterId}`]}
                    >
                      <FaCheck className="w-4 h-4" />
                      Accept Final Proof
                    </button>
                    <button
                      onClick={() => {
                        closePreviewModal();
                        handleRejectProof(
                          typeof selectedProof.adDetails?.adId === 'object'
                            ? selectedProof.adDetails?.adId?._id
                            : selectedProof.adDetails?.adId,
                          selectedProof.reporterId
                        );
                      }}
                      className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center gap-2"
                      disabled={processingProof[`${selectedProof.adDetails?.adId}-${selectedProof.reporterId}`]}
                    >
                      <FaTimes className="w-4 h-4" />
                      Reject Final Proof
                    </button>
                  </>
                )}

                <button
                  onClick={closePreviewModal}
                  className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 rounded-2xl shadow-xl p-8 mb-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold mb-2 flex items-center gap-3">
                <FaPlay className="text-blue-300" />
                Running Advertisements
              </h1>
              <p className="text-blue-100 text-lg">
                Monitor and manage running advertisements with proof submissions and view tracking
              </p>
              <div className="mt-4 flex items-center gap-4">
                <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                  <span className="text-sm text-blue-100">Total Running Ads</span>
                  <div className="text-2xl font-bold text-white">{searchText ? getFilteredAds().length : runningAds.length}</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                  <span className="text-sm text-blue-100">Active Campaigns</span>
                  <div className="text-2xl font-bold text-white">{runningAds.length}</div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/pendingposts")}
              >
                <FaCheck className="w-4 h-4" />
                Pending
              </button>
              <button
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/approvedposts")}
              >
                <FaCheck className="w-4 h-4" />
                Approved
              </button>
              <button
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/rejectedposts")}
              >
                <FaTimes className="w-4 h-4" />
                Rejected
              </button>
              <button
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/completedposts")}
              >
                <FaCheck className="w-4 h-4" />
                Completed
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section - Compact */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-4 border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white text-sm"
                placeholder="Search by Ad ID, Iinsaf ID, Channel Name, or Reporter Name..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="bg-blue-50 rounded-lg px-3 py-1.5">
              <span className="text-xs text-blue-600 font-medium">Page {currentPage}/{totalPages}</span>
            </div>
            <div className="bg-green-50 rounded-lg px-3 py-1.5">
              <span className="text-xs text-green-600 font-medium">{filteredAds.length} Results</span>
            </div>
            {completedAds.length > 0 && (
              <button
                onClick={() => setShowCompletedAds(!showCompletedAds)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1.5 ${showCompletedAds
                  ? "bg-green-500 hover:bg-green-600 text-white"
                  : "bg-gray-500 hover:bg-gray-600 text-white"
                  }`}
              >
                <FaCheck className="w-3 h-3" />
                {showCompletedAds ? "Hide" : "Show"} Completed ({completedAds.length})
              </button>
            )}
            <button
              onClick={fetchRunningAds}
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1.5"
            >
              <FaDownload className="w-3 h-3" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center gap-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-blue-500 animate-spin"></div>
              <div className="absolute inset-2 rounded-full border-4 border-transparent border-r-indigo-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
            </div>
            <p className="text-gray-600 font-medium">Loading running ads...</p>
          </div>
        </div>
      ) : runningAds.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
            <FaPlay className="w-12 h-12 text-blue-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            No Running Advertisements
          </h3>
          <p className="text-gray-500 text-lg mb-6">
            You don't have any running advertisements at the moment.
          </p>
          <button
            onClick={fetchRunningAds}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
          >
            Refresh
          </button>
        </div>
      ) : filteredAds.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
            <FaSearch className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            No Results Found
          </h3>
          <p className="text-gray-500 text-lg mb-6">
            No advertisements match your search criteria.
          </p>
          <button
            onClick={() => setSearchText("")}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="space-y-4" id="ads-table">
          {paginatedAds.map((ad) =>
            ad.proofs && ad.proofs.length > 0 ? (
              <div key={ad.adId} className={`bg-white rounded-xl shadow-md border overflow-hidden ${ad.proofs && ad.proofs.length > 0 && ad.proofs.every(proof => proof.status === "completed")
                ? "border-green-200 bg-green-50"
                : "border-gray-200"
                }`}>
                {/* Ad Header - Ultra Compact */}
                <div className={`px-3 py-2 border-b ${ad.proofs && ad.proofs.length > 0 && ad.proofs.every(proof => proof.status === "completed")
                  ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
                  : "bg-gradient-to-r from-blue-50 to-indigo-50 border-gray-200"
                  }`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className={`rounded px-2 py-1 font-bold text-xs ${ad.proofs && ad.proofs.length > 0 && ad.proofs.every(proof => proof.status === "completed")
                        ? "bg-green-500 text-white"
                        : "bg-blue-500 text-white"
                        }`}>
                        {ad.adId}
                      </div>
                      <div className="text-[10px] text-gray-600">
                        <span className="font-semibold">Proofs:</span> {ad.proofs.length} / {ad.requiredReporter}
                      </div>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${ad.proofs && ad.proofs.length > 0 && ad.proofs.every(proof => proof.status === "completed")
                        ? "bg-green-100 text-green-800"
                        : "bg-blue-100 text-blue-800"
                        }`}>
                        {ad.proofs && ad.proofs.length > 0 && ad.proofs.every(proof => proof.status === "completed")
                          ? "Completed"
                          : "Running"
                        }
                      </span>
                    </div>
                    <button
                      onClick={() => handleViewAdDetails(ad.adId)}
                      className="px-2 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded text-[10px] font-medium transition-colors flex items-center gap-1"
                      title="View Full Advertisement Details"
                    >
                      <FaInfoCircle className="w-2.5 h-2.5" />
                      Details
                    </button>
                  </div>
                </div>

                {/* Table Content - Compact */}
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-1 py-1 text-center text-[9px] font-semibold text-gray-600 uppercase tracking-wider w-10">
                          #
                        </th>
                        <th className="px-1 py-1 text-left text-[9px] font-semibold text-gray-600 uppercase tracking-wider">
                          <FaUser className="inline w-2.5 h-2.5 mr-0.5" />
                          User ID
                        </th>
                        <th className="px-1 py-1 text-left text-[9px] font-semibold text-gray-600 uppercase tracking-wider">
                          <FaFilter className="inline w-2.5 h-2.5 mr-0.5" />
                          Type
                        </th>
                        <th className="px-1 py-1 text-left text-[9px] font-semibold text-gray-600 uppercase tracking-wider">
                          <FaGlobe className="inline w-2.5 h-2.5 mr-0.5" />
                          Platform
                        </th>
                        <th className="px-1 py-1 text-left text-[9px] font-semibold text-gray-600 uppercase tracking-wider">
                          Channel
                        </th>
                        <th className="px-1 py-1 text-left text-[9px] font-semibold text-gray-600 uppercase tracking-wider">
                          <FaVideo className="inline w-2.5 h-2.5 mr-0.5" />
                          Video
                        </th>
                        <th className="px-1 py-1 text-left text-[9px] font-semibold text-gray-600 uppercase tracking-wider">
                          <FaEye className="inline w-2.5 h-2.5 mr-0.5" />
                          Views
                        </th>
                        <th className="px-1 py-1 text-left text-[9px] font-semibold text-gray-600 uppercase tracking-wider">
                          <FaClock className="inline w-2.5 h-2.5 mr-0.5" />
                          Duration
                        </th>
                        <th className="px-1 py-1 text-left text-[9px] font-semibold text-gray-600 uppercase tracking-wider">
                          <FaMoneyBillWave className="inline w-2.5 h-2.5 mr-0.5" />
                          Credit
                        </th>
                        <th className="px-1 py-1 text-left text-[9px] font-semibold text-gray-600 uppercase tracking-wider">
                          <FaCalendar className="inline w-2.5 h-2.5 mr-0.5" />
                          Date
                        </th>
                        <th className="px-1 py-1 text-left text-[9px] font-semibold text-gray-600 uppercase tracking-wider w-20">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ad.proofs.map((proof, proofIndex) => {
                        // Calculate serial number based on advertisement index (not proof index)
                        const currentAdIndex = paginatedAds.indexOf(ad);
                        const serialNumber = ((currentPage - 1) * rowsPerPage) + currentAdIndex + 1;
                        return (
                          <tr key={proofIndex} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                            <td className="px-1 py-1 text-center">
                              <span className="inline-flex items-center justify-center w-6 h-5 bg-blue-100 text-blue-800 rounded font-bold text-[10px] border border-blue-200">
                                {serialNumber}
                              </span>
                            </td>
                            <td className="px-1 py-1">
                              <div className="bg-blue-100 text-blue-800 rounded px-1 py-0.5 font-mono text-[10px]">
                                {proof.iinsafId || "N/A"}
                              </div>
                            </td>
                            <td className="px-1 py-1">
                              <span className={`inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium ${proof.iinsafId && proof.iinsafId.startsWith('INF')
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                                }`}>
                                {proof.iinsafId && proof.iinsafId.startsWith('INF') ? '🎯 INF' : '📰 REP'}
                              </span>
                            </td>
                            <td className="px-1 py-1 text-[10px] text-gray-900">
                              {proof.platform || "N/A"}
                            </td>
                            <td className="px-1 py-1 text-[10px] text-gray-900 font-medium max-w-[80px] truncate" title={proof.channelName || "N/A"}>
                              {proof.channelName || "N/A"}
                            </td>
                            <td className="px-1 py-1">
                              {proof.videoLink ? (
                                <a
                                  href={proof.videoLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-blue-500 text-white rounded text-[10px] font-medium hover:bg-blue-600 transition-colors"
                                >
                                  <FaPlay className="w-1.5 h-1.5" />
                                  Watch
                                </a>
                              ) : (
                                <span className="text-gray-400 text-[10px]">N/A</span>
                              )}
                            </td>
                            <td className="px-1 py-1">
                              <div className="space-y-0.5">
                                <ViewChecker
                                  platform={proof.platform}
                                  videoLink={proof.videoLink}
                                  adId={ad.adId}
                                  proofIndex={proofIndex}
                                />
                              </div>
                            </td>
                            <td className="px-1 py-1 text-[10px] text-gray-900">
                              {proof.duration || "N/A"}
                            </td>
                            <td className="px-1 py-1">
                              <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800">
                                ₹{ad.finalReporterPrice ? Number(ad.finalReporterPrice).toFixed(2) : "N/A"}
                              </span>
                            </td>
                            <td className="px-1 py-1 text-[10px] text-gray-500 whitespace-nowrap">
                              {proof.submittedAt
                                ? new Date(proof.submittedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' + new Date(proof.submittedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                                : "N/A"}
                            </td>
                            <td className="px-1 py-1">
                              <div className="flex flex-col gap-1">
                                {/* Always show Preview button */}
                                <button
                                  className="px-1 py-0.5 bg-purple-500 text-white rounded text-[10px] font-medium hover:bg-purple-600 transition-colors flex items-center justify-center gap-0.5"
                                  onClick={() => handlePreviewProof(proof, ad)}
                                  title="Preview Proof"
                                >
                                  <FaEye className="w-2 h-2" />
                                </button>

                                {/* Show Accept/Reject buttons only when final proof is submitted */}
                                {proof.status === "submitted" ? (
                                  <>
                                    <button
                                      className="px-1 py-0.5 bg-green-500 text-white rounded text-[10px] font-medium hover:bg-green-600 transition-colors flex items-center justify-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                                      onClick={() => handleApproveProof(ad.adId, proof.reporterId)}
                                      disabled={processingProof[`${ad.adId}-${proof.reporterId}`]}
                                      title="Approve Final Proof"
                                    >
                                      {processingProof[`${ad.adId}-${proof.reporterId}`] ? (
                                        <FaSpinner className="animate-spin w-2 h-2" />
                                      ) : (
                                        <FaCheck className="w-2 h-2" />
                                      )}
                                    </button>
                                    <button
                                      className="px-1 py-0.5 bg-red-500 text-white rounded text-[10px] font-medium hover:bg-red-600 transition-colors flex items-center justify-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                                      onClick={() => handleRejectProof(ad.adId, proof.reporterId)}
                                      disabled={processingProof[`${ad.adId}-${proof.reporterId}`]}
                                      title="Reject Final Proof"
                                    >
                                      {processingProof[`${ad.adId}-${proof.reporterId}`] ? (
                                        <FaSpinner className="animate-spin w-2 h-2" />
                                      ) : (
                                        <FaTimes className="w-2 h-2" />
                                      )}
                                    </button>
                                  </>
                                ) : proof.status === "pending" ? (
                                  <>
                                    <button
                                      className="px-1 py-0.5 bg-blue-500 text-white rounded text-[10px] font-medium hover:bg-blue-600 transition-colors flex items-center justify-center"
                                      onClick={() => {
                                        if (!proof.screenshot) {
                                          console.warn("No screenshot found for this proof.");
                                          return;
                                        }
                                        const imageUrl = proof.screenshot.startsWith('http')
                                          ? proof.screenshot
                                          : `${process.env.REACT_APP_API_BASE_URL}/${proof.screenshot.replace(/\\/g, "/")}`;
                                        setCurrentScreenshot(imageUrl);
                                        setShowScreenshotModal(true);
                                      }}
                                      title="View Initial Proof"
                                    >
                                      <FaCamera className="w-2 h-2" />
                                    </button>
                                    <div className="flex gap-0.5">
                                      <button
                                        className="flex-1 px-1 py-0.5 bg-green-500 text-white rounded text-[10px] font-medium hover:bg-green-600 transition-colors flex items-center justify-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                                        onClick={() => handleApproveInitialProof(ad.adId, proof.reporterId)}
                                        disabled={processingProof[`${ad.adId}-${proof.reporterId}`]}
                                        title="Approve"
                                      >
                                        {processingProof[`${ad.adId}-${proof.reporterId}`] ? (
                                          <FaSpinner className="animate-spin w-2 h-2" />
                                        ) : (
                                          <FaCheck className="w-2 h-2" />
                                        )}
                                      </button>
                                      <button
                                        className="flex-1 px-1 py-0.5 bg-red-500 text-white rounded text-[10px] font-medium hover:bg-red-600 transition-colors flex items-center justify-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                                        onClick={() => handleRejectInitialProof(ad.adId, proof.reporterId)}
                                        disabled={processingProof[`${ad.adId}-${proof.reporterId}`]}
                                        title="Reject"
                                      >
                                        {processingProof[`${ad.adId}-${proof.reporterId}`] ? (
                                          <FaSpinner className="animate-spin w-2 h-2" />
                                        ) : (
                                          <FaTimes className="w-2 h-2" />
                                        )}
                                      </button>
                                    </div>
                                  </>
                                ) : proof.status === "approved" ? (
                                  <div className="bg-green-50 border border-green-200 rounded p-1.5">
                                    <div className="flex items-center gap-1 mb-1">
                                      <FaCheck className="w-2.5 h-2.5 text-green-600" />
                                      <span className="text-[10px] font-semibold text-green-800">Approved</span>
                                    </div>
                                    <button
                                      className="w-full px-1 py-0.5 bg-blue-500 text-white rounded text-[10px] font-medium hover:bg-blue-600 transition-colors flex items-center justify-center gap-0.5"
                                      onClick={() => {
                                        if (!proof.screenshot) {
                                          console.warn("No screenshot found for this proof.");
                                          return;
                                        }
                                        const imageUrl = proof.screenshot.startsWith('http')
                                          ? proof.screenshot
                                          : `${process.env.REACT_APP_API_BASE_URL}/${proof.screenshot.replace(/\\/g, "/")}`;
                                        setCurrentScreenshot(imageUrl);
                                        setShowScreenshotModal(true);
                                      }}
                                      title="View Initial Proof"
                                    >
                                      <FaCamera className="w-2 h-2" />
                                    </button>
                                  </div>
                                ) : proof.status === "completed" ? (
                                  <div className="bg-green-50 border border-green-200 rounded p-1.5">
                                    <div className="flex items-center gap-1 mb-1">
                                      <FaCheck className="w-2.5 h-2.5 text-green-600" />
                                      <span className="text-[10px] font-semibold text-green-800">Completed</span>
                                    </div>
                                    <div className="flex gap-0.5">
                                      <button
                                        className="flex-1 px-1 py-0.5 bg-blue-500 text-white rounded text-[10px] font-medium hover:bg-blue-600 transition-colors"
                                        onClick={() => {
                                          if (!proof.screenshot) {
                                            console.warn("No screenshot found for this proof.");
                                            return;
                                          }
                                          const imageUrl = proof.screenshot.startsWith('http')
                                            ? proof.screenshot
                                            : `${process.env.REACT_APP_API_BASE_URL}/${proof.screenshot.replace(/\\/g, "/")}`;
                                          setCurrentScreenshot(imageUrl);
                                          setShowScreenshotModal(true);
                                        }}
                                        title="View Initial"
                                      >
                                        <FaCamera className="w-2 h-2 mx-auto" />
                                      </button>
                                      {proof.completedTaskScreenshot && (
                                        <button
                                          className="flex-1 px-1 py-0.5 bg-green-500 text-white rounded text-[10px] font-medium hover:bg-green-600 transition-colors"
                                          onClick={() => {
                                            const imageUrl = proof.completedTaskScreenshot.startsWith('http')
                                              ? proof.completedTaskScreenshot
                                              : `${process.env.REACT_APP_API_BASE_URL}/${proof.completedTaskScreenshot.replace(/\\/g, "/")}`;
                                            setCurrentScreenshot(imageUrl);
                                            setShowScreenshotModal(true);
                                          }}
                                          title="View Final"
                                        >
                                          <FaCheck className="w-2 h-2 mx-auto" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ) : proof.status === "rejected" ? (
                                  <div className="bg-red-50 border border-red-200 rounded p-1.5">
                                    <div className="flex items-center gap-1 mb-1">
                                      <FaTimes className="w-2.5 h-2.5 text-red-600" />
                                      <span className="text-[10px] font-semibold text-red-800">Rejected</span>
                                    </div>
                                    {(proof.initialProofRejectNote || proof.adminRejectNote) && (
                                      <div className="text-[9px] text-red-700 mt-0.5 line-clamp-2" title={proof.initialProofRejectNote || proof.adminRejectNote}>
                                        {proof.initialProofRejectNote || proof.adminRejectNote}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="px-1 py-0.5 bg-gray-100 text-gray-600 rounded text-center">
                                    <span className="text-[10px] font-medium">Pending</span>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div key={ad.adId} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                  <FaPlay className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Ad ID: {ad.adId}
                </h3>
                <p className="text-gray-500">No proofs submitted</p>
              </div>
            )
          )}
        </div>
      )}
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <nav className="inline-flex items-center space-x-2 bg-white rounded-2xl shadow-lg p-2 border border-gray-100">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-blue-600 hover:bg-blue-50 hover:text-blue-700'
                }`}
            >
              Previous
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === pageNum
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                  }`}
                aria-current={currentPage === pageNum ? 'page' : undefined}
              >
                {pageNum}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === totalPages
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-blue-600 hover:bg-blue-50 hover:text-blue-700'
                }`}
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${confirmAction === 'approve' || confirmAction === 'approve_initial' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                {confirmAction === 'approve' || confirmAction === 'approve_initial' ? (
                  <FaCheck className="w-6 h-6 text-green-600" />
                ) : (
                  <FaTimes className="w-6 h-6 text-red-600" />
                )}
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {confirmAction === 'approve' ? 'Approve Final Proof?' :
                confirmAction === 'approve_initial' ? 'Approve Initial Proof?' :
                  confirmAction === 'reject_initial' ? 'Reject Initial Proof?' : 'Reject Final Proof?'}
            </h3>

            <p className="text-gray-600 text-center mb-6">
              {confirmAction === 'approve'
                ? 'Are you sure you want to approve this final proof? The user will be credited and moved to completed ads.'
                : confirmAction === 'approve_initial'
                  ? 'Are you sure you want to approve this initial proof? The user can proceed with the task.'
                  : confirmAction === 'reject_initial'
                    ? 'Are you sure you want to reject this initial proof? The user must resubmit the initial proof.'
                    : 'Are you sure you want to reject this final proof? The user will be notified to resubmit.'
              }
            </p>

            {/* Admin Note Field for Rejection */}
            {(confirmAction === 'reject' || confirmAction === 'reject_initial') && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rejection (Required):
                </label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder={confirmAction === 'reject_initial'
                    ? "Please provide a reason for rejecting the initial proof..."
                    : "Please provide a reason for rejecting this proof..."}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                  rows={3}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This note will be visible to the user
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                  setConfirmData(null);
                  setAdminNote("");
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                No, Cancel
              </button>
              <button
                onClick={
                  confirmAction === 'approve' ? confirmApproveProof :
                    confirmAction === 'approve_initial' ? confirmApproveInitialProof :
                      confirmAction === 'reject_initial' ? confirmRejectInitialProof :
                        confirmRejectProof
                }
                className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors ${confirmAction === 'approve' || confirmAction === 'approve_initial'
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-red-500 hover:bg-red-600'
                  }`}
              >
                Yes, {confirmAction === 'approve' || confirmAction === 'approve_initial' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advertisement Full Details Modal */}
      {showAdDetailsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto relative border border-gray-200">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-700 text-white p-6 rounded-t-2xl relative">
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold bg-white bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                onClick={closeAdDetailsModal}
                title="Close"
              >
                &times;
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <FaInfoCircle className="w-6 h-6 text-indigo-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">
                    Full Advertisement Details
                  </h3>
                  <p className="text-indigo-100 text-sm">
                    Complete information submitted by advertiser
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8">
              {loadingAdDetails ? (
                <div className="flex justify-center items-center h-64">
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative w-16 h-16">
                      <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-indigo-500 animate-spin"></div>
                      <div className="absolute inset-2 rounded-full border-4 border-transparent border-r-purple-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                    </div>
                    <p className="text-gray-600 font-medium">Loading advertisement details...</p>
                  </div>
                </div>
              ) : adDetails ? (
                <>
                  {/* Information Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {/* Basic Information Card */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-500 text-white rounded-lg p-2">
                          <FaFileAlt className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Basic Information</h5>
                          <p className="text-sm text-gray-600">Advertisement details</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad ID</span>
                          <span className="font-mono text-sm text-gray-900 break-all">{adDetails._id}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad Type</span>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                            {adDetails.adType || "N/A"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Media Type</span>
                          <span className="font-medium text-gray-900">{adDetails.mediaType || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Media Description</span>
                          <span className="font-medium text-gray-900">{adDetails.mediaDescription || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad Length</span>
                          <span className="font-medium text-gray-900">{adDetails.adLength || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Status</span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${adDetails.status === "approved" ? "bg-green-100 text-green-800" :
                            adDetails.status === "rejected" ? "bg-red-100 text-red-800" :
                              adDetails.status === "running" ? "bg-blue-100 text-blue-800" :
                                adDetails.status === "completed" ? "bg-purple-100 text-purple-800" :
                                  "bg-yellow-100 text-yellow-800"
                            }`}>
                            {adDetails.status || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Campaign Details Card */}
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-green-500 text-white rounded-lg p-2">
                          <FaCalendar className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Campaign Details</h5>
                          <p className="text-sm text-gray-600">Timeline and requirements</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-green-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Start Date</span>
                          <span className="font-medium text-gray-900">{adDetails.startDate || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-green-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">End Date</span>
                          <span className="font-medium text-gray-900">{adDetails.endDate || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-green-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Required Views</span>
                          <span className="text-xl font-bold text-green-600">
                            {adDetails.requiredViews ? Number(adDetails.requiredViews).toLocaleString() : "N/A"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-green-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Required Reporters</span>
                          <span className="text-lg font-bold text-green-600">
                            {adDetails.requiredReporter || "N/A"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-green-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Base View</span>
                          <span className="text-lg font-bold text-green-600">
                            {adDetails.baseView || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Financial Details Card */}
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-2xl border border-yellow-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-yellow-500 text-white rounded-lg p-2">
                          <FaMoneyBillWave className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Financial Details</h5>
                          <p className="text-sm text-gray-600">Budget and pricing</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Total Budget</span>
                          <span className="text-2xl font-bold text-yellow-600">
                            ₹{adDetails.totalCost || "0.00"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Subtotal</span>
                          <span className="text-lg font-bold text-yellow-600">
                            ₹{adDetails.subtotal || "0.00"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">GST</span>
                          <span className="text-lg font-bold text-yellow-600">
                            ₹{adDetails.gst || "0.00"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Admin Commission</span>
                          <span className="text-lg font-bold text-yellow-600">
                            ₹{adDetails.adminCommission ? Number(adDetails.adminCommission).toFixed(2) : "0.00"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Reporter Price</span>
                          <span className="text-lg font-bold text-yellow-600">
                            ₹{adDetails.finalReporterPrice ? Number(adDetails.finalReporterPrice).toFixed(2) : "0.00"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Commission Rate</span>
                          <span className="text-lg font-bold text-yellow-600">
                            {adDetails.adCommissionRate || 0}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Location Details Card */}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-500 text-white rounded-lg p-2">
                          <FaMapMarkerAlt className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Location Details</h5>
                          <p className="text-sm text-gray-600">Geographic targeting</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">State</span>
                          <span className="font-medium text-gray-900">{adDetails.adState || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">City</span>
                          <span className="font-medium text-gray-900">{adDetails.adCity || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Pincode</span>
                          <span className="font-medium text-gray-900">{adDetails.pincode || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Preference State</span>
                          <span className="font-medium text-gray-900">{adDetails.pfState || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Preference Cities</span>
                          <span className="font-medium text-gray-900">
                            {Array.isArray(adDetails.pfCities) ? adDetails.pfCities.join(", ") : adDetails.pfCities || "N/A"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">All States</span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${adDetails.allStates ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                            {adDetails.allStates ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Advertiser Information Card */}
                    {adDetails.owner && (
                      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-2xl border border-indigo-200">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="bg-indigo-500 text-white rounded-lg p-2">
                            <FaUser className="w-4 h-4" />
                          </div>
                          <div>
                            <h5 className="font-semibold text-gray-800">Advertiser Information</h5>
                            <p className="text-sm text-gray-600">Submitted by advertiser</p>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Name</span>
                            <span className="font-medium text-gray-900">{adDetails.owner.name || "N/A"}</span>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Email</span>
                            <span className="font-medium text-gray-900">{adDetails.owner.email || "N/A"}</span>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Mobile</span>
                            <span className="font-medium text-gray-900">{adDetails.owner.mobile || "N/A"}</span>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">IINSAF ID</span>
                            <span className="font-medium text-gray-900">{adDetails.owner.iinsafId || "N/A"}</span>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Organization</span>
                            <span className="font-medium text-gray-900">{adDetails.owner.organization || "N/A"}</span>
                          </div>
                          {adDetails.owner.state && (
                            <div className="bg-white rounded-lg p-3 border border-indigo-200">
                              <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Location</span>
                              <span className="font-medium text-gray-900">
                                {adDetails.owner.city || ""} {adDetails.owner.city && adDetails.owner.state ? ", " : ""}{adDetails.owner.state || ""}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Admin Targeting Card */}
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl border border-orange-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-orange-500 text-white rounded-lg p-2">
                          <FaFilter className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Admin Targeting</h5>
                          <p className="text-sm text-gray-600">Targeting configuration</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-orange-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Target User Type</span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${adDetails.userType === 'influencer' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                            {adDetails.userType === 'influencer' ? '🎯 Influencer' : '📰 Reporter'}
                          </span>
                        </div>
                        {adDetails.adminSelectState && adDetails.adminSelectState.length > 0 && (
                          <div className="bg-white rounded-lg p-3 border border-orange-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Admin Selected States</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {adDetails.adminSelectState.map((state, idx) => (
                                <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                                  {state}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {adDetails.adminSelectCities && adDetails.adminSelectCities.length > 0 && (
                          <div className="bg-white rounded-lg p-3 border border-orange-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Admin Selected Cities</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {adDetails.adminSelectCities.slice(0, 5).map((city, idx) => (
                                <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                                  {city}
                                </span>
                              ))}
                              {adDetails.adminSelectCities.length > 5 && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                  +{adDetails.adminSelectCities.length - 5} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {adDetails.adminSelectPincode && (
                          <div className="bg-white rounded-lg p-3 border border-orange-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Admin Selected Pincode</span>
                            <span className="font-medium text-gray-900">{adDetails.adminSelectPincode}</span>
                          </div>
                        )}
                        {adDetails.reporterId && adDetails.reporterId.length > 0 && (
                          <div className="bg-white rounded-lg p-3 border border-orange-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Targeted Reporters</span>
                            <span className="font-medium text-gray-900">{adDetails.reporterId.length} reporter(s) selected</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Media Section */}
                  {(adDetails.imageUrl || adDetails.videoUrl) && (
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-2xl border border-gray-200 mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-gray-500 text-white rounded-lg p-2">
                          {adDetails.mediaType === "image" ? (
                            <FaCamera className="w-4 h-4" />
                          ) : (
                            <FaVideo className="w-4 h-4" />
                          )}
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Advertisement Media</h5>
                          <p className="text-sm text-gray-600">{adDetails.mediaType === "image" ? "Image content" : "Video content"}</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-center min-h-[300px] bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                          {adDetails.mediaType === "image" && adDetails.imageUrl && (
                            <img
                              src={adDetails.imageUrl}
                              alt={adDetails.mediaDescription || "Advertisement"}
                              className="max-w-full h-auto max-h-[280px] rounded-lg shadow-sm border border-gray-200 object-contain"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/fallback-image.jpg";
                              }}
                            />
                          )}
                          {adDetails.mediaType === "video" && adDetails.videoUrl && (
                            <video
                              src={adDetails.videoUrl}
                              controls
                              className="max-w-full h-auto max-h-[280px] rounded-lg shadow-sm border border-gray-200"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-6 rounded-2xl border border-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-slate-500 text-white rounded-lg p-2">
                        <FaClock className="w-4 h-4" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-800">Timestamps</h5>
                        <p className="text-sm text-gray-600">Important dates and times</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Created At</span>
                        <span className="font-medium text-gray-900">
                          {adDetails.createdAt ? new Date(adDetails.createdAt).toLocaleString() : "N/A"}
                        </span>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Updated At</span>
                        <span className="font-medium text-gray-900">
                          {adDetails.updatedAt ? new Date(adDetails.updatedAt).toLocaleString() : "N/A"}
                        </span>
                      </div>
                      {adDetails.approvedAt && (
                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Approved At</span>
                          <span className="font-medium text-gray-900">
                            {new Date(adDetails.approvedAt).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {adDetails.acceptBefore && (
                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Accept Before</span>
                          <span className="font-medium text-gray-900">
                            {new Date(adDetails.acceptBefore).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-8 flex justify-end gap-4">
                    <button
                      onClick={closeAdDetailsModal}
                      className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl"
                    >
                      Close
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No advertisement details available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default RunningPost;
